import React, { useEffect, useState } from "react";

import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import {
  FORGOT_PASS,
  FORGOT_PASS_TILE,
  LOGIN,
  LOGIN_APPLE,
  LOGIN_GOOGLE,
  LOGIN_TO,
  SEND,
} from "../../../utils/strings";
import EOMButton from "../../../components/EOMButton";
import { isTabletOrMobileSize } from "../../../utils/queries";
import theme from "../../../utils/theme";
import { AppleYellow, GoogleYellow } from "../../../assets/images";
import Line from "../../../components/Line";
import {
  loginFirebase,
  loginWithApple,
  loginWithGoogle,
  resetPassFirebase,
  startPhoneVerification,
  phoneSignIn,
} from "../../../auth/operations";
import { emptyBasket } from "../../../slices/appSlice";
import { ADMIN_DASHBOARD, DASHBOARD_GYM_OWNERS, DASHBOARD_MEMBERS } from "../../../utils/routes";
import {
  Container,
  ForgotPassword,
  Or,
  OrContainer,
  Subcontainer,
  SocialLoginContainer,
  Title,
  CustomizedTextField,
  ErrorMessage,
  Box,
} from "./styles";
import { getUserMembershipStatusCall, checkDuplicatesCall } from "../../../api/apiOperations";
import DashboardMembers from "../../app/DashboardMembers";

const Login = () => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [forgotPassEmail, setForgotPassEmail] = useState<string>("");
  const [forgotPass, setForgotPass] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [authMode, setAuthMode] = useState<"email" | "phone">("email");
  const [phoneDigits, setPhoneDigits] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [phoneStep, setPhoneStep] = useState<"phone" | "code">("phone");
  const [phoneLoading, setPhoneLoading] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>("");
  const [phoneNotFound, setPhoneNotFound] = useState<boolean>(false);

  const isTabletOrMobile = useMediaQuery({ query: isTabletOrMobileSize });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { memberLoggedIn, gymOwnerLoggedIn } = useSelector(
    (state: RootState) => state.auth
  );

  const handleLogin = async () => {
    setLoading(true);
    const result: any = await loginFirebase(username, password);
  
    if (result?.error?.error) {
      setError(true);
      setErrorMessage(result?.error?.message);
      setLoading(false);
    } else {
      setError(false);
      setErrorMessage("");
      setLoading(false);
      dispatch(emptyBasket());
  
      const role = result?.role;

      // console.log("Role:", role);
  
      if (role === "admin") {
        navigate(ADMIN_DASHBOARD);
      } else if (role === "partner") {
        navigate(DASHBOARD_GYM_OWNERS);
      } else if (role === "member") {
        navigate(DASHBOARD_MEMBERS);
      } else {
        setError(true);
        setErrorMessage("Could not determine user role.");
      }
    }
  };
  

  const handleForgotPass = async () => {
    await resetPassFirebase(forgotPassEmail);
    setForgotPass(!forgotPass);
    setForgotPassEmail("");
  };

  const formatUSPhone = (digits: string) => {
    const d = digits.slice(0, 10);
    const a = d.slice(0, 3);
    const b = d.slice(3, 6);
    const c = d.slice(6, 10);
    if (d.length > 6) return `(${a}) ${b}-${c}`;
    if (d.length > 3) return `(${a}) ${b}`;
    if (d.length > 0) return `(${a}`;
    return "";
  };

  const handleSendCode = async () => {
    if (phoneDigits.length !== 10) {
      setPhoneError("Enter a valid 10-digit phone number.");
      return;
    }
    setPhoneLoading(true);
    setPhoneError("");
    setPhoneNotFound(false);
    const e164 = `+1${phoneDigits}`;

    const dupe: any = await checkDuplicatesCall(e164);
    if (dupe?.isPhoneUnique !== false) {
      setPhoneLoading(false);
      setPhoneNotFound(true);
      setPhoneError("No account found with this phone number.");
      return;
    }

    const result: any = await startPhoneVerification(e164);
    setPhoneLoading(false);
    if (result?.error) {
      setPhoneError(result.error.message);
      return;
    }
    setPhoneStep("code");
  };

  const handleVerifyCode = async () => {
    if (otp.length !== 6) {
      setPhoneError("Enter the 6-digit code.");
      return;
    }
    setPhoneLoading(true);
    setPhoneError("");
    setPhoneNotFound(false);
    const result: any = await phoneSignIn(`+1${phoneDigits}`, otp);
    setPhoneLoading(false);
    if (result?.error) {
      if (result.error.noAccount) {
        setPhoneNotFound(true);
        setPhoneStep("phone");
        setOtp("");
      }
      setPhoneError(result.error.message);
      return;
    }
    dispatch(emptyBasket());
    const role = result?.role;
    if (role === "admin") {
      navigate(ADMIN_DASHBOARD);
    } else if (role === "partner") {
      navigate(DASHBOARD_GYM_OWNERS);
    } else if (role === "member") {
      navigate(DASHBOARD_MEMBERS);
    } else {
      setPhoneError("Could not determine your account.");
    }
  };

  const handleClickShowPassword = () =>
    setShowPassword((show: boolean) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (!username || !password) {
      setError(false);
      setErrorMessage("");
    }
  }, [username, password]);

  useEffect(() => {
    if (memberLoggedIn) {
      setLoading(false);
      navigate(DASHBOARD_MEMBERS);
    }
    if (gymOwnerLoggedIn) {
      setLoading(false);
      navigate(DASHBOARD_GYM_OWNERS);
    }
  }, [memberLoggedIn, gymOwnerLoggedIn]);

  return (
    <Container>
      <Title>{forgotPass ? FORGOT_PASS_TILE : LOGIN_TO}</Title>
      {forgotPass ? (
        <Subcontainer>
          <CustomizedTextField
            value={forgotPassEmail}
            onChange={(e: any) => setForgotPassEmail(e.target.value)}
            label="Enter your email address"
            placeholder="Email address"
            InputLabelProps={{
              shrink: true,
            }}
            autoFocus
            type="email"
            name="email"
            autoComplete="email"
          />
          <EOMButton
            title={SEND}
            onPress={handleForgotPass}
            disabled={!forgotPassEmail}
            buttonStyle={{
              borderRadius: 3,
              height: 15,
              marginTop: 50,
              alignContent: "center",
            }}
            titleStyle={{
              color: "black",
              fontSize: 14,
              textAlign: "center",
            }}
          />
        </Subcontainer>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: 8,
              width: "100%",
              marginTop: 12,
              marginBottom: 0,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setAuthMode("email");
                setPhoneError("");
              }}
              style={{
                flex: 1,
                padding: "10px 16px",
                borderRadius: 24,
                border: "none",
                cursor: "pointer",
                fontFamily: "Aileron, sans-serif",
                fontSize: 14,
                backgroundColor: authMode === "email" ? "#222222" : "#EFEFEF",
                color: authMode === "email" ? "white" : "#222222",
              }}
            >
              Email
            </button>
            <button
              type="button"
              onClick={() => {
                setAuthMode("phone");
                setError(false);
                setErrorMessage("");
              }}
              style={{
                flex: 1,
                padding: "10px 16px",
                borderRadius: 24,
                border: "none",
                cursor: "pointer",
                fontFamily: "Aileron, sans-serif",
                fontSize: 14,
                backgroundColor: authMode === "phone" ? "#222222" : "#EFEFEF",
                color: authMode === "phone" ? "white" : "#222222",
              }}
            >
              Phone
            </button>
          </div>
          {authMode === "email" ? (
          <>
          <Subcontainer>
            <Box>
              <CustomizedTextField
                value={username}
                onChange={(e: any) => setUsername(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' && username && password && !loading) handleLogin();
                }}
                label="E-mail"
                placeholder="Email Address"
                InputLabelProps={{
                  shrink: true,
                }}
                autoFocus
                type="email"
                name="email"
                autoComplete="username"
                error={error}
              />
              {error && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </Box>
            <Box>
              <CustomizedTextField
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' && username && password && !loading) handleLogin();
                }}
                label="Password"
                placeholder="Password"
                InputLabelProps={{
                  shrink: true,
                }}
                name="password"
                autoComplete="current-password"
                error={error}
                type={!showPassword ? "password" : "text"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        style={{ color: theme.colors.grey }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {error && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </Box>
            <ForgotPassword onClick={() => setForgotPass(!forgotPass)}>
              {FORGOT_PASS}
            </ForgotPassword>
          </Subcontainer>
          <EOMButton
            title={LOGIN}
            onPress={handleLogin}
            disabled={!username || !password}
            buttonStyle={{
              borderRadius: 24,
              width: "100%",
              padding: "14px 16px",
              marginTop: 24,
              backgroundColor: "#222222",
              border: "none",
            }}
            titleStyle={{
              color: "white",
              fontSize: 14,
              fontFamily: "Aileron, sans-serif",
              textAlign: "center",
            }}
            loading={loading}
          />
          {/* <OrContainer>
            <Line />
            <Or>or</Or>
            <Line />
          </OrContainer>
          <SocialLoginContainer>
            <EOMButton
              title={LOGIN_APPLE}
              onPress={() => loginWithApple()}
              outlined
              buttonStyle={{
                borderRadius: 3,
                width: "86%",
                height: 15,
                marginTop: 30,
                alignContent: "center",
                border: `1px solid ${theme.templateColors.lightYellow}`,
              }}
              titleStyle={{
                color: theme.colors.white,
                fontSize: isTabletOrMobile ? 12 : 14,
                textAlign: "center",
              }}
              icon={AppleYellow}
            />
            <EOMButton
              title={LOGIN_GOOGLE}
              onPress={() => loginWithGoogle()}
              outlined
              buttonStyle={{
                borderRadius: 3,
                width: "86%",
                height: 15,
                marginTop: 30,
                alignContent: "center",
                border: `1px solid ${theme.templateColors.lightYellow}`,
              }}
              titleStyle={{
                color: theme.colors.white,
                fontSize: isTabletOrMobile ? 12 : 14,
                textAlign: "center",
              }}
              icon={GoogleYellow}
            />
          </SocialLoginContainer> */}
          </>
          ) : (
            <Subcontainer style={{ paddingTop: 0 }}>
              {phoneStep === "phone" ? (
                <>
                  <Box>
                    <CustomizedTextField
                      value={formatUSPhone(phoneDigits)}
                      onChange={(e: any) => {
                        setPhoneDigits(
                          e.target.value.replace(/\D/g, "").slice(0, 10)
                        );
                        setPhoneError("");
                        setPhoneNotFound(false);
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" && !phoneLoading) handleSendCode();
                      }}
                      label="Phone number"
                      placeholder="(555) 123-4567"
                      InputLabelProps={{ shrink: true }}
                      autoFocus
                      type="tel"
                      autoComplete="off"
                      error={!!phoneError}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <span style={{ color: "#000000", fontFamily: "Aileron, sans-serif", fontSize: 14 }}>
                              +1
                            </span>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}
                    {phoneNotFound && (
                      <div
                        style={{
                          marginTop: 8,
                          fontFamily: "Aileron, sans-serif",
                          fontSize: 12,
                          color: "#555555",
                        }}
                      >
                        If you have an account,{" "}
                        <span
                          onClick={() => {
                            setAuthMode("email");
                            setPhoneError("");
                            setPhoneNotFound(false);
                            setPhoneStep("phone");
                            setOtp("");
                          }}
                          style={{ color: "#3487d3", cursor: "pointer", fontWeight: 500 }}
                        >
                          sign in with email
                        </span>
                        . Otherwise, please create an account.
                      </div>
                    )}
                  </Box>
                  <EOMButton
                    title="Send code"
                    onPress={handleSendCode}
                    disabled={phoneDigits.length !== 10}
                    buttonStyle={{
                      borderRadius: 24,
                      width: "100%",
                      padding: "14px 16px",
                      marginTop: 24,
                      backgroundColor: "#222222",
                      border: "none",
                    }}
                    titleStyle={{
                      color: "white",
                      fontSize: 14,
                      fontFamily: "Aileron, sans-serif",
                      textAlign: "center",
                    }}
                    loading={phoneLoading}
                  />
                  <ForgotPassword as="div" style={{ cursor: "default" }}>
                    Use the phone number linked to your account.
                  </ForgotPassword>
                </>
              ) : (
                <>
                  <Box>
                    <CustomizedTextField
                      value={otp}
                      onChange={(e: any) => {
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                        setPhoneError("");
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" && !phoneLoading) handleVerifyCode();
                      }}
                      label="Verification code"
                      placeholder="6-digit code"
                      InputLabelProps={{ shrink: true }}
                      autoFocus
                      type="tel"
                      autoComplete="one-time-code"
                      error={!!phoneError}
                    />
                    {phoneError && <ErrorMessage>{phoneError}</ErrorMessage>}
                  </Box>
                  <EOMButton
                    title="Verify & sign in"
                    onPress={handleVerifyCode}
                    disabled={otp.length !== 6}
                    buttonStyle={{
                      borderRadius: 24,
                      width: "100%",
                      padding: "14px 16px",
                      marginTop: 24,
                      backgroundColor: "#222222",
                      border: "none",
                    }}
                    titleStyle={{
                      color: "white",
                      fontSize: 14,
                      fontFamily: "Aileron, sans-serif",
                      textAlign: "center",
                    }}
                    loading={phoneLoading}
                  />
                  <ForgotPassword
                    onClick={() => {
                      setPhoneStep("phone");
                      setOtp("");
                      setPhoneError("");
                    }}
                  >
                    Change number
                  </ForgotPassword>
                </>
              )}
            </Subcontainer>
          )}
        </>
      )}
    </Container>
  );
};

export default Login;
